import {Nodes, NodeType, DisconnectedNodes} from 'common/src/modules/nodes/nodes.types';
import {DateTime} from 'luxon';
import {
  NodeIssue,
  ThirdPartyTrackingIssue,
  PlacementIssue,
} from '../../components/CLCardsMap/CardsMapContainer/CardsMapContainer.types';
import ObjectStatus from '../enums/static/ObjectStatus';
import {CLIssues, CLIssuesMessage} from '../issues/issues.types';
import {CampaignOptimization, RemoteCampaignOptimization} from '../optimization/optimization.types';
import {
  ExternalPlatform,
  FacebookExternalCampaignPage,
  LinkedInExternalCampaignPage,
  MockSocialExternalCampaignPage,
  SnapchatExternalCampaignPage,
  SocialAssetType,
  SocialNetworkPage,
  TiktokExternalCampaignPage,
  YoutubeExternalCampaignPage,
} from '../social/social.types';
import {TaxonomyType} from '../taxonomy/taxonomy.types';
import {CampaignMetric} from '../analytics/analytics.types';
import {SocialCampaignFlowStatus} from '../externalAds/externalAds.types';
import {CreativeFormatSelectionMethod} from './campaigns.enums';
import {CampaignStep} from '../campaignWorkflow/campaignWorkflow.enums';

type ServerCampaign = {
  id: number;
  landingPage: string[];
  name?: string;
  type: CampaignType;
  status: number;
  premiumServices?: PremiumServices;
  generation: CampaignGeneration;
  cpm?: number;
  subType: CampaignSubType;
};

type Campaign = ServerCampaign & {
  /** States the active state of the campaign. This property is calculated on client side */
  isActive: boolean;
  isSocial?: boolean;
};

type ServerSocialCampaign = ServerCampaign & {
  optimization?: RemoteCampaignOptimization;
  issues?: CLIssues;
  version?: number;
};

type FCServerCampaign = ServerCampaign & {
  campaignTypeSpecificObj: {
    destinationAdPlatform?: number;
    skipoffset?: number;
  };
  dataSourcesConfiguration: {
    includedDataSources: string[];
  };
  tag1: CampaignTag;
  tag2: CampaignTag;
  tag3: CampaignTag;
  supportedDisplayAdSizes: string[];
  description?: string;
  allowedAttributionSignalIds: number[];
  skipOffset?: number;
  isGenerateAdsRequired: boolean;
  creativeFormatSelectionMethod?: CreativeFormatSelectionMethod;
};

type BaseSocialCampaign = Omit<ServerSocialCampaign, 'optimization'> &
  Campaign & {
    optimizationData?: CampaignOptimization;
    tag1: CampaignTag;
    tag2: CampaignTag;
    tag3: CampaignTag;
    supportedDisplayAdSizes: string[];
    description?: string;
    premiumServices: PremiumServices;
    isGenerateAdsRequired: boolean;
  };

type FacebookCampaign = BaseSocialCampaign &
  FacebookExternalCampaignPage & {
    type: CampaignType.FacebookVideoAds;
    subType: SocialCampaignSubType.FacebookStandard | SocialCampaignSubType.FacebookDPA;
  };

type YoutubeCampaign = BaseSocialCampaign &
  YoutubeExternalCampaignPage & {
    type: CampaignType.YoutubeVideoAds;
    subType: SocialCampaignSubType.YoutubeGoogleAds | SocialCampaignSubType.YoutubeDV360;
    youtubeChannel?: SocialNetworkPage;
  };

type TiktokCampaign = BaseSocialCampaign &
  TiktokExternalCampaignPage & {
    type: CampaignType.TikTokAds;
    subType: SocialCampaignSubType.TiktokStandard;
  };

type SnapchatCampaign = BaseSocialCampaign &
  SnapchatExternalCampaignPage & {
    type: CampaignType.SnapchatAds;
    subType: SocialCampaignSubType.SnapchatStandard;
  };

type LinkedInCampaign = BaseSocialCampaign &
  LinkedInExternalCampaignPage & {
    type: CampaignType.LinkedInAds;
    subType: SocialCampaignSubType.LinkedInStandard;
  };

type MockSocialCampaign = BaseSocialCampaign &
  MockSocialExternalCampaignPage & {
    type: CampaignType.MockSocial;
    subType: SocialCampaignSubType.MockSocialStandard;
  };

type SocialCampaign =
  | FacebookCampaign
  | YoutubeCampaign
  | TiktokCampaign
  | SnapchatCampaign
  | LinkedInCampaign
  | MockSocialCampaign;

type FCCampaign = FCServerCampaign & Campaign;

interface CampaignSocialData {
  accountId: string;
  campaignId: string;
  platform: number;
  platformName: string;
}

interface PremiumServices {
  highAccuracyLocation: {
    enable: boolean;
  };
  sports: {
    enable: boolean;
  };
  weather: {
    enable: boolean;
  };
  advancedTrackerSignals: {
    enable: boolean;
  };
  contextualDecisioning: {
    enable: boolean;
  };
}

interface CampaignTag {
  name: string;
  value: string;
}

// TODO: move all enums from this file to campaigns.enums.ts
enum CampaignGeneration {
  Old = 0,
  FlightControl = 1,
}

type SlimCampaignTag = {
  Key: string;
  Value: string;
};

type ServerSlimCampaign = {
  id: number;
  name: string;
  status: ObjectStatus;
  type: CampaignType;
  subType: CampaignSubType;
  isDPA: boolean;
  generation: CampaignGeneration;
  tags: SlimCampaignTag[];
  createTime: string;
  createdByUserName: string;
  versionData:
    | (CampaignVersionData & {
        number: number;
        activationStart: string;
      })
    | null;
  description: string;
} & (
  | {
      type: SocialCampaignType;
      flowStatus: SocialCampaignFlowStatus;
    }
  | {
      type: ProgrammaticCampaignType;
      flowStatus: null;
    }
);

type SlimCampaign = Omit<ServerSlimCampaign, 'createTime'> & {
  isSocial: boolean;
  isActive: boolean;
  createTime: DateTime;
};

export type SlimCampaignWithMetrics = SlimCampaign & {metrics: CampaignMetric};

enum CampaignType {
  DynamicBanners = 0,
  InBannerVideoAds = 5,
  VideoAds = 10,
  AudioAds = 15,
  ThirdPartyAdTracking = 30,
  FacebookVideoAds = 40,
  TikTokAds = 45,
  YoutubeVideoAds = 50,
  SnapchatAds = 55,
  NativeAds = 60,
  InteractiveVideoAds = 70,
  DoohImageAd = 80,
  LinkedInAds = 90,
  MockSocial = 95,
}

// Only export AiCampaignType once to avoid conflicts
enum AiCampaignType {
  DynamicBanners = 'Display',
  InBannerVideoAds = 'In Banner Video Ads',
  VideoAds = 'Video Ads',
  AudioAds = 'Audio Ads',
  ThirdPartyAdTracking = 'Third Party Ad Tracking',
  FacebookVideoAds = 'Facebook Video Ads',
  TikTokAds = 'TikTok Ads',
  YoutubeVideoAds = 'Youtube Video Ads',
  SnapchatAds = 'Snapchat Ads',
  NativeAds = 'Native Ads',
  InteractiveVideoAds = 'Interactive Video Ads',
  DoohImageAd = 'Dooh Image Ad',
  LinkedInAds = 'LinkedIn Ads',
  MockSocial = 'Mock Social',
}

// TODO: Rename this to CampaignType, delete current CampaignType enum and use campaignTypeConfigs[campaignType].id to get campaign type
// This requires refactoring a lot of code that uses the current CampaignType enum
enum CampaignTypeName {
  DynamicBanners = 'DynamicBanners',
  InBannerVideoAds = 'InBannerVideoAds',
  VideoAds = 'VideoAds',
  InteractiveVideoAds = 'InteractiveVideoAds',
  ThirdPartyAdTracking = 'ThirdPartyAdTracking',
  FacebookVideoAds = 'FacebookVideoAds',
  TikTokAds = 'TikTokAds',
  YoutubeVideoAds = 'YoutubeVideoAds',
  SnapchatAds = 'SnapchatAds',
  LinkedInAds = 'LinkedIn',
  NativeAds = 'NativeAds',
  DoohImageAd = 'DoohImageAd',
  AudioAds = 'AudioAds',
  MockSocial = 'MockSocial',
}

enum SocialCampaignSubType {
  FacebookStandard = 100,
  FacebookDPA = 101,
  YoutubeGoogleAds = 200,
  YoutubeDV360 = 201,
  TiktokStandard = 300,
  SnapchatStandard = 400,
  LinkedInStandard = 500,
  MockSocialStandard = 600,
}

enum AiSocialCampaignSubType {
  FacebookStandard = 'Facebook Standard',
  FacebookDPA = 'Facebook DPA',
  YoutubeGoogleAds = 'Youtube Google Ads',
  YoutubeDV360 = 'Youtube DV360',
  TiktokStandard = 'Tiktok Standard',
  SnapchatStandard = 'Snapchat Standard',
  LinkedInStandard = 'LinkedIn Standard',
  MockSocial = 'Mock Social Standard',
}

enum NonSocialCampaignSubType {
  NONE = 0,
}

type SocialCampaignType =
  | CampaignType.FacebookVideoAds
  | CampaignType.YoutubeVideoAds
  | CampaignType.TikTokAds
  | CampaignType.SnapchatAds
  | CampaignType.LinkedInAds
  | CampaignType.MockSocial;

type ProgrammaticCampaignType =
  | CampaignType.DynamicBanners
  | CampaignType.InBannerVideoAds
  | CampaignType.VideoAds
  | CampaignType.AudioAds
  | CampaignType.ThirdPartyAdTracking
  | CampaignType.NativeAds
  | CampaignType.InteractiveVideoAds
  | CampaignType.DoohImageAd;

type CampaignSubType = SocialCampaignSubType | NonSocialCampaignSubType;

enum SocialCampaignSubTypeName {
  FacebookStandard = 'FacebookStandard',
  FacebookDPA = 'FacebookDPA',
  YoutubeGoogleAds = 'YoutubeGoogleAds',
  YoutubeDV360 = 'YoutubeDV360',
  TiktokStandard = 'TiktokStandard',
  SnapchatStandard = 'SnapchatStandard',
  LinkedInStandard = 'LinkedInStandard',
  MockSocialStandard = 'MockSocialStandard',
}

enum NonSocialCampaignSubTypeName {
  NONE = 'NONE',
}

// TODO: do we really need the different enums for social and non-social?
type CampaignSubTypeName = SocialCampaignSubTypeName | NonSocialCampaignSubTypeName;

// TODO: remove this enum and use campaignTypeConfigs instead
enum SocialCampaignSubTypeTitle {
  FacebookStandard = 'Standard',
  FacebookDPA = 'DPA',
  GoogleAds = 'Google Ads',
  DisplayAndVideo = 'DV360',
  SnapchatStandard = 'Snapchat',
  TikTokStandard = 'Standard',
  MockSocial = 'MockSocialStandard',
}

export interface CampaignIntegrationBase {
  dspType?: string;
  data?: {
    displayBlockingEnabled?: false;
    mediaFilter?: 0;
    universalMonitoringEnabled?: true;
    videoOmniTagBlockingEnabled?: false;
    videoOmniTagEnabled?: false;
    videoOmniTagFilteringEnabled?: false;
    plaid?: string;
  };
}

interface RemoteCampaignIntegration extends CampaignIntegrationBase {
  id: number;
  assetType: SocialAssetType;
  extAccountId: string;
  extAccountName: string;
  extCampaignId: string;
  extCampaignName: string;
  status: number;
  platform: number;
  clientIntegrationId: number;
  state: string;
}

interface CampaignVersionData {
  isActivated?: boolean;
  isDraft?: boolean;
  isPendingActivation?: boolean;
  isCampaignAwaitActivation?: boolean;
  name: string;
  activationTime: {
    start: DateTime | null;
  };
}

/**
 * This structure represents a social campaign which is connected to an account that was connected to clinch campaign...
 * */
interface SocialCampaignIntegration {
  id: string;
  name: string;
}

interface CampaignUsage {
  campaignId: number;
  isUsedInActiveCampaignVersion: boolean;
  isCampaignActive: boolean;
}

enum DTCampaignVersionType {
  Draft = 'draft',
  Latest = 'latest',
}

enum CampaignVersionStatus {
  PendingActivation = 'Pending Activation',
  Active = 'Active',
  Draft = 'Draft',
  Deactivated = 'Deactivated',
  Saved = 'Saved',
  Locked = 'Locked',
}

interface SocialCampaignStatuses {
  adGroupsStatuses: Record<string, ObjectStatus>;
  campaignsStatuses: Record<string, ObjectStatus>;
}

export interface CampaignIntegrationIssue extends CLIssuesMessage {
  platform: ExternalPlatform;
}

export interface CampaignIssues {
  campaignLogicIssues: NodeIssue[];
  thirdPartyTrackersIssues: ThirdPartyTrackingIssue[];
  campaignIntegrationIssues: CampaignIntegrationIssue[];
  placementIssues?: PlacementIssue[];
}

export enum NodeDuplicationRejectionReason {
  SocialCampaignDoesNotSupportLocation = 'SocialCampaignDoesNotSupportLocation',
  SocialCampaignDoesNotSupportPlacements = 'SocialCampaignDoesNotSupportPlacements',
  SocialCampaignDoesNotSupportUserProfile = 'SocialCampaignDoesNotSupportUserProfile',
  SocialCampaignDoesNotSupportDsps = 'SocialCampaignDoesNotSupportDsps',
  ProgrammaticCampaignDoesNotSupportAdGroups = 'ProgrammaticCampaignDoesNotSupportAdGroups',
  ProductSelectionStrategyIsNotSupported = 'ProductSelectionStrategyIsNotSupported',
  CreativeCannotBeCopiedBetweenDifferentCampaignTypes = 'CreativeCannotBeCopiedBetweenDifferentCampaignTypes',
}

export const NodeDuplicationRejectionReasonNames: Record<NodeDuplicationRejectionReason, string> = {
  [NodeDuplicationRejectionReason.SocialCampaignDoesNotSupportLocation]:
    'Social Campaign does not support Location',
  [NodeDuplicationRejectionReason.SocialCampaignDoesNotSupportPlacements]:
    'Social Campaign does not support Placements',
  [NodeDuplicationRejectionReason.SocialCampaignDoesNotSupportUserProfile]:
    'Social Campaign does not support User Profile',
  [NodeDuplicationRejectionReason.SocialCampaignDoesNotSupportDsps]:
    'Social Campaign does not support DSPs',
  [NodeDuplicationRejectionReason.ProgrammaticCampaignDoesNotSupportAdGroups]:
    'Programmatic Campaign does not support Ad Groups',
  [NodeDuplicationRejectionReason.ProductSelectionStrategyIsNotSupported]:
    'Product Selection Strategy is not supported',
  [NodeDuplicationRejectionReason.CreativeCannotBeCopiedBetweenDifferentCampaignTypes]:
    'Creative cannot be copied between different Campaign Types',
};

export interface DuplicateNodeResponse {
  type: NodeType;
  id: number;
  invalidReason: NodeDuplicationRejectionReason;
}

// Config info per campaign type
type CampaignTypeConfig = {
  type: CampaignTypeName;
  label: string;
  id: CampaignType;
  isSocial: boolean;
  isDuplicationAllowed: boolean;
  testIsEnabled: () => boolean;
  subtypes: CampaignSubtypeConfig[];
};

// Config info per campaign subtype
type CampaignSubtypeConfig = {
  type: CampaignSubTypeName;
  label: string;
  id: CampaignSubType;
  testIsEnabled: () => boolean;
};

export interface RemoteCampaignSignal {
  value: string;
  label: string;
  category: string;
  taxonomyKey: string;
  taxonomyType?: TaxonomyType;
  description: string;
}

export interface CampaignSignal extends Omit<RemoteCampaignSignal, 'category'> {
  category: TaxonomyType;
}

enum CampaignTreeType {
  Campaign = 'campaign',
  Concept = 'concept',
}

interface CampaignTreeNodes {
  [CampaignTreeType.Concept]: Nodes;
  [CampaignTreeType.Campaign]: Nodes;
}

interface DisconnectedCampaignTreeNodes {
  [CampaignTreeType.Concept]: DisconnectedNodes;
  [CampaignTreeType.Campaign]: DisconnectedNodes;
}

enum DisableCampaignChangesReason {
  None,
  NoPermissions,
  SavedVersion,
  WorkflowStepInFreezeMode,
  RenderingVariations,
  CampaignIsNotEditable,
}

type AiProductSelectionConditionSignal = {
  name: string;
  value: string;
  category: string;
};

enum PlacementCustomParamValue {
  None = 0,
  AnimationLength = 5,
  BizId = 15,
  StoreGroupId = 20,
  ItemId = 30,
  ItemIdRandom = 31,
  MinimumProductsToShow = 35,
  CatId = 40,
  Custom1 = 50,
  Custom2 = 60,
  Custom3 = 70,
  Custom4 = 73,
  Custom5 = 76,
  Custom6 = 77,
  Custom7 = 78,
  Custom8 = 79,
  ExternalCampaignId = 80,
  ExternalSiteId = 90,
  ExternalPlacementId = 100,
  ExternalAdId = 102,
  ExternalCreativeId = 104,
  ExternalCreativeName = 105,
  AudienceId = 110,
  DoubleVerifyExternalCampaignId = 120,
  DoubleVerifyExternalSiteId = 121,
  DoubleVerifyExternalPlacementId = 122,
  StartDateTime = 130,
  EndDateTime = 131,
  RequirePingForAttribution = 132,
}

enum PlacementCustomParamName {
  None = 'None',
  AnimationLength = 'animationLength',
  BizId = 'bizId',
  StoreGroupId = 'storeGroupId',
  ItemId = 'itemId',
  ItemIdRandom = 'itemIdRandom',
  MinimumProductsToShow = 'minimumProductsToShow',
  CatId = 'catId',
  Custom1 = 'custom1',
  Custom2 = 'custom2',
  Custom3 = 'custom3',
  Custom4 = 'custom4',
  Custom5 = 'custom5',
  Custom6 = 'custom6',
  Custom7 = 'custom7',
  Custom8 = 'custom8',
  ExternalCampaignId = 'externalCampaignId',
  ExternalSiteId = 'externalSiteId',
  ExternalPlacementId = 'externalPlacementId',
  ExternalAdId = 'externalAdId',
  ExternalCreativeId = 'externalCreativeId',
  ExternalCreativeName = 'externalCreativeName',
  AudienceId = 'audienceId',
  DoubleVerifyExternalCampaignId = 'DoubleVerifyExternalCampaignId',
  DoubleVerifyExternalSiteId = 'DoubleVerifyExternalSiteId',
  DoubleVerifyExternalPlacementId = 'DoubleVerifyExternalPlacementId',
  StartDateTime = 'StartDateTime',
  EndDateTime = 'EndDateTime',
  RequirePingForAttribution = 'RequirePingForAttribution',
}

const placementCustomParamValueToName: Record<PlacementCustomParamValue, string> = {
  [PlacementCustomParamValue.None]: 'None',
  [PlacementCustomParamValue.AnimationLength]: 'Creative Animation Loop Length',
  [PlacementCustomParamValue.BizId]: 'Show location Id ',
  [PlacementCustomParamValue.StoreGroupId]: 'Location Group Id',
  [PlacementCustomParamValue.ItemId]: 'Product Ids to Show',
  [PlacementCustomParamValue.ItemIdRandom]: 'Product Ids to show randomly',
  [PlacementCustomParamValue.MinimumProductsToShow]: 'Minimum products to show',
  [PlacementCustomParamValue.CatId]: 'Category to show',
  [PlacementCustomParamValue.Custom1]: 'Custom 1',
  [PlacementCustomParamValue.Custom2]: 'Custom 2',
  [PlacementCustomParamValue.Custom3]: 'Custom 3',
  [PlacementCustomParamValue.Custom4]: 'Custom 4',
  [PlacementCustomParamValue.Custom5]: 'Custom 5',
  [PlacementCustomParamValue.Custom6]: 'Custom 6',
  [PlacementCustomParamValue.Custom7]: 'Custom 7',
  [PlacementCustomParamValue.Custom8]: 'Custom 8',
  [PlacementCustomParamValue.ExternalCampaignId]: 'External Campaign ID',
  [PlacementCustomParamValue.ExternalSiteId]: 'External Site ID',
  [PlacementCustomParamValue.ExternalPlacementId]: 'External Placement ID',
  [PlacementCustomParamValue.ExternalAdId]: 'External Ad ID',
  [PlacementCustomParamValue.ExternalCreativeId]: 'External Creative ID',
  [PlacementCustomParamValue.ExternalCreativeName]: 'External Creative Name',
  [PlacementCustomParamValue.AudienceId]: 'Audience ID',
  [PlacementCustomParamValue.DoubleVerifyExternalCampaignId]: 'DoubleVerify External Campaign ID',
  [PlacementCustomParamValue.DoubleVerifyExternalSiteId]: 'DoubleVerify External Site ID',
  [PlacementCustomParamValue.DoubleVerifyExternalPlacementId]: 'DoubleVerify External Placement ID',
  [PlacementCustomParamValue.StartDateTime]: 'Start Date & Time',
  [PlacementCustomParamValue.EndDateTime]: 'End Date & Time',
  [PlacementCustomParamValue.RequirePingForAttribution]: 'Requires Ping For Attribution',
};

type RemoteCampaignStepsInformation = {
  cpNumOfComments: number;
  dtNumOfIssues: number;
  dtNumOfComments: number;
  numOfFailedAds: number;
  adSetsNumOfIssues: number;
};

type BaseStepInfo = {
  issues: number;
  comments: number;
};

type CampaignsStepsInformation = Record<CampaignStep, BaseStepInfo>;

export {
  Campaign,
  CampaignGeneration,
  CampaignSocialData,
  CampaignSubType,
  CampaignSubtypeConfig,
  CampaignSubTypeName,
  CampaignTag,
  CampaignTreeNodes,
  CampaignTreeType,
  CampaignType,
  CampaignTypeConfig,
  CampaignTypeName,
  CampaignUsage,
  CampaignVersionData,
  CampaignVersionStatus,
  DisableCampaignChangesReason,
  DTCampaignVersionType,
  FacebookCampaign,
  FCCampaign,
  FCServerCampaign,
  LinkedInCampaign,
  MockSocialCampaign,
  NonSocialCampaignSubType,
  NonSocialCampaignSubTypeName,
  PremiumServices,
  RemoteCampaignIntegration,
  ServerCampaign,
  ServerSlimCampaign,
  ServerSocialCampaign,
  SlimCampaign,
  SlimCampaignTag,
  SocialCampaign,
  SocialCampaignIntegration,
  SocialCampaignStatuses,
  SocialCampaignSubType,
  SocialCampaignSubTypeName,
  SocialCampaignSubTypeTitle,
  SocialCampaignType,
  AiCampaignType,
  TiktokCampaign,
  YoutubeCampaign,
  SnapchatCampaign,
  AiSocialCampaignSubType,
  DisconnectedCampaignTreeNodes,
  AiProductSelectionConditionSignal,
  RemoteCampaignStepsInformation,
  CampaignsStepsInformation,
  PlacementCustomParamValue,
  PlacementCustomParamName,
  placementCustomParamValueToName,
};
